import { track } from '@/config/observability';
import type { LessonSettings, StagedLink, StagedText } from '@/lib/api-client';

interface GenerationRequest {
  lessonId: string;
  settings?: LessonSettings;
  sourceIds?: string[];
  files?: File[];
  links?: StagedLink[];
  texts?: StagedText[];
  focusTopic?: string;
  isDeepDive: boolean;
}

/**
 * Analytics for a lesson kicked off from Home. Counts and enums only - the
 * topic, URLs and pasted text a learner typed are never sent.
 */
export function trackGenerationRequested(req: GenerationRequest): void {
  const files = req.files ?? [];
  const links = req.links ?? [];
  const texts = req.texts ?? [];

  files.forEach(file =>
    track('source_added', { where: 'home', type: 'file', mime_type: file.type })
  );
  links.forEach(() => track('source_added', { where: 'home', type: 'link' }));
  texts.forEach(() => track('source_added', { where: 'home', type: 'text' }));

  track('lesson_generation_requested', {
    lesson_id: req.lessonId,
    flow: req.settings?.flow ?? 'default',
    depth: req.settings?.options?.depth ?? 'default',
    file_count: files.length,
    link_count: links.length,
    text_count: texts.length,
    library_source_count: req.sourceIds?.length ?? 0,
    has_focus_topic: Boolean(req.focusTopic),
    is_deep_dive: req.isDeepDive,
  });
}

/** A generation the backend refused (safety filter, limits, errors). */
export function trackGenerationRejected(err: unknown): void {
  const e = err as {
    status?: number;
    data?: { type?: string; reason?: string };
  } | null;
  track('lesson_generation_rejected', {
    status: e?.status ?? null,
    reason: e?.data?.reason ?? e?.data?.type ?? 'unknown',
  });
}
